/* global unityInstance */
import { useEffect, useState, useContext, useRef } from 'react';
import { SelectedPatientContext } from "../SelectedPatientContext.tsx";
import { API_PATH } from '../Constants.ts';
import { CustomField } from '../field.js';
import scriptjs from 'scriptjs';

export const ScenarioConfiguration = () => {
    const [scenarios, setScenarios] = useState([]);
    const [configurations, setConfigurations] = useState(null);
    const {selectedPatientId} = useContext(SelectedPatientContext);

    const loadedUnity = useRef(false);
    const isUnityReady = () =>
        typeof createUnityInstance === 'function' &&
        unityInstance != null &&
        unityInstance.Module != null &&
        !unityInstance.Module.aborted;

    const sessionCodePollIntervalMs = 1000;
    const sessionCodePollIntervalIdRef = useRef(null);
    const [sessionCode, setSessionCode]  = useState();
    const sessionCodeRef = useRef();
    const isValidSessionCode = (code) => /^[A-Z0-9]{6}$/.test(code);

    useEffect(() => {
        if (sessionCode === sessionCodeRef.current) return;
        if (!isValidSessionCode(sessionCode)) {
            console.warn("Invalid session code: " + sessionCode);
            return;
        }

        sessionCodeRef.current = sessionCode;
        unityInstance.SendMessage('Controller', 'JoinSession', sessionCode);
        console.log(`Therapist joined session ${sessionCode} of patient ${selectedPatientId}`);
    }, [sessionCode]);

    const isUnityLoaded = () => {
        const warnings = [];
        if (!loadedUnity.current) warnings.push('Unity loading script not finished yet.');
        if (!isUnityReady())      warnings.push('Unity instance not ready yet.');
        warnings.forEach(msg => console.warn(msg));
        return warnings.length === 0;
    }

    useEffect(() => {
        if (selectedPatientId == null) return;

        const controller = new AbortController();
        const { signal } = controller;

        const refreshSession = async ({ selectedPatientId, signal }) => {
            if (!isUnityLoaded()) return;

            fetch(`${API_PATH.SESSIONS}?id=${selectedPatientId}`, { signal })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText} — ${res.url}`);
                return res.json();
            })
            .then(jsonResponse => {
                if (signal.aborted) return;
                setSessionCode(jsonResponse.session_code);
            })
            .catch(err => {
                console.error(err);
            });
        }

        sessionCodePollIntervalIdRef.current = setInterval(
            () => refreshSession({ selectedPatientId, signal }),
            sessionCodePollIntervalMs
        );

        return () => {
            controller.abort();
            clearInterval(sessionCodePollIntervalIdRef.current);
        }
    }, [selectedPatientId]);

    useEffect(() => {
        scriptjs('resources/setup-streaming.js', () => { loadedUnity.current = true });

        const handleScenariosUpdated = (event) => setScenarios(event.detail);
        const handleScenarioPropertyUpdated = (event) =>
            console.log("Variable changed: ", event.detail.variableName, ": ", event.detail.variableValue);

        window.addEventListener('scenariosUpdated', handleScenariosUpdated);
        window.addEventListener('scenarioPropertyUpdated', handleScenarioPropertyUpdated);

        return () => {
            window.removeEventListener('scenariosUpdated', handleScenariosUpdated);
            window.removeEventListener('scenarioPropertyUpdated', handleScenarioPropertyUpdated);
        };
    }, []);


    const handleScenarioChange = (scenarioName) => {
        window.SetScenario(scenarioName);
        const selectedScenario = scenarios.find(scenario => scenario.name === scenarioName);
        setConfigurations(selectedScenario ? selectedScenario.properties : null);
    }

    return (
        <>
            <CustomField
                property={{ type: 'enum', name: 'Scenario', values: scenarios }}
                onChange={handleScenarioChange}
            />
            {configurations && configurations.map((configuration) => (
                <CustomField
                    key={configuration.name}
                    property={configuration}
                    onChange={(chosenValue) => window.SetVariable(configuration.name, chosenValue)}
                />
            ))}
        </>
    );
};